<script setup>
  import { onMounted, ref, watch } from "vue";
  import axios from "axios";
  import ProjectsSort from "./ProjectsSort.vue";
  import ProjectsFilters from "./ProjectsFilters.vue";
  import SearchComponent from "./SearchComponent.vue";
  import Pagination from "./Pagination.vue";
  import ProjectCard from "./ProjectCard.vue";
  import ViewComponent from "./ViewComponent.vue";


  const isLoading = ref(true);
  const isTransitioning = ref(false);
  const trans = (key) => {
    return _.get(window.translations, key, key);
  };
  const projects = ref([]);
  const pagination = ref({
    current_page: 1,
    per_page: 5,
    total: 0
  });
  const searchQuery = ref("");
  const sortFilters = ref({
    name: [
      { name: "A-Z", value: "asc", selected: false },
      { name: "Z-A", value: "desc", selected: false }
    ],
    mostRecent: { name: "Mais Recente", value: "desc", selected: false },
  });

  const perPageOptions = ref([
    { label: "12", value: 12 },
    { label: "24", value: 24 },
    { label: "48", value: 48 },
  ]);

  const selectedPerPage = ref(perPageOptions.value[0]);
  const props = defineProps({
    orderType: {
      type: Array,
      default: []
    },
  });

  const projectsLayout = ref("grid-3-columns");

  const handleFilters = (filter) => {
    const url = new URL(window.location.href);

    url.searchParams.delete("name");
    url.searchParams.delete("mostRecent");
    url.searchParams.delete("type");

    let filterValue;
    if (filter?.value) {
      if (typeof filter.value === 'object' && filter.value.value) {
        filterValue = filter.value.value;
      } else {
        filterValue = filter.value;
      }
    }

    if (typeof filterValue === 'string') {
      url.searchParams.set(filter.type, filterValue);
    }

    window.history.replaceState(null, "", url.toString());
    pagination.value.current_page = 1;
    fetchProjects();
  };

  const handleUpdateQuery = (queryString, newTerm) => {
    const url = new URL(window.location.href);

    url.searchParams.delete("q");
    url.searchParams.delete("name");
    url.searchParams.delete("mostRecent");
    url.searchParams.delete("type");

    if (newTerm) {
      url.searchParams.set(queryString, newTerm);
    }

    window.history.replaceState(null, "", url.toString());
    pagination.value.current_page = 1;
    fetchProjects();
  };

  const handleLayoutChange = (newLayout) => {
    projectsLayout.value = newLayout;
    const url = new URL(window.location);
    url.searchParams.set("perRow", newLayout === "grid-1-column" ? 1 : 3);
    window.history.replaceState(null, "", url.toString());
    fetchProjects();
  };

  const handlePerPageChange = (newPerPageObject) => {
    const url = new URL(window.location);
    pagination.value.per_page = newPerPageObject.value;
    pagination.value.current_page = 1;
    url.searchParams.set('perPage', newPerPageObject.value);
    url.searchParams.set('page', 1);
    window.history.replaceState(null, "", url.toString());
    fetchProjects();
  };

  const loadQueryStringFromUrl = () => {
    const url = new URL(window.location);
    if (url.searchParams.has("q")) {
      searchQuery.value = url.searchParams.get("q");
    } else {
      searchQuery.value = "";
    }

    if (url.searchParams.has("page")) {
      pagination.value.current_page = parseInt(url.searchParams.get("page"));
    }

    if (url.searchParams.has("perPage")) {
      const perPageFromUrl = parseInt(url.searchParams.get("perPage"));
      const foundOption = perPageOptions.value.find(option => option.value === perPageFromUrl);
      if (foundOption) {
        selectedPerPage.value = foundOption;
        pagination.value.per_page = perPageFromUrl;
      }
    } else {
      selectedPerPage.value = perPageOptions.value[0];
      pagination.value.per_page = perPageOptions.value[0].value;
    }

    if (url.searchParams.has("perRow")) {
      const perRow = parseInt(url.searchParams.get("perRow"));
      projectsLayout.value = perRow === 1 ? "grid-1-column" : "grid-3-columns";
    }
  };

  const fetchProjects = async () => {
    isTransitioning.value = false;
    isLoading.value = true;

    try {
      const url = new URL(window.location.href);
      const requestData = {
        url: window.location.pathname,
        page: pagination.value.current_page,
        perPage: pagination.value.per_page,
      };

      if (url.searchParams.has("q")) { requestData.q = url.searchParams.get("q"); }
      if (url.searchParams.has("name")) { requestData.name = url.searchParams.get("name"); }
      if (url.searchParams.has("mostRecent")) { requestData.mostRecent = url.searchParams.get("mostRecent"); }
      if (url.searchParams.has("type")) { requestData.type = url.searchParams.get("type"); }

      const csrfToken = document.head.querySelector("meta[name=\"csrf-token\"]").content;
      const { data } = await axios.post(`/integrations/topcer-projects`, requestData, {
        headers: {
          "X-CSRF-TOKEN": csrfToken
        }
      });

      projects.value = data.articles || [];
      pagination.value.current_page = data.paginator.current_page;
      pagination.value.per_page = data.paginator.per_page;
      pagination.value.total = data.paginator.total;

    } catch (error) {
      console.error("Houve um erro ao buscar os projetos!", error);
    } finally {
      setTimeout(() => {
        isLoading.value = false;
        isTransitioning.value = true;
      }, 300);
    }
  };

  watch(() => pagination.value.current_page, () => {
    const url = new URL(window.location.href);
    url.searchParams.set('page', pagination.value.current_page);
    window.history.replaceState(null, "", url.toString());
    fetchProjects();
  });

  onMounted(() => {
    loadQueryStringFromUrl();
    fetchProjects();
  });
</script>

<template>
  <div class="container-projects">
    <Transition name="fade-overlay">
      <div v-if="isLoading" id="loading-overlay">
        <div class="spinner"></div>
      </div>
    </Transition>
    <div class="container-search">
      <search-component
        @update:searchTerm="term => handleUpdateQuery('q', term)"
      ></search-component>
    </div>
    <div class="container-filters">
      <div class="container-order">
        <projects-filters
          :sort-filters="sortFilters"
          :order-type="orderType"
          @update:sortOrder="handleFilters"
        ></projects-filters>
      </div>
      <div class="container-sort">
        <div class="container-grid">
          <projects-sort
            :selected-layout="projectsLayout"
            @update:layout="handleLayoutChange"
          ></projects-sort>
        </div>
        <div class="container-view">
          <view-component
            :per-page="selectedPerPage"
            :per-page-options="perPageOptions"
            @update:perPage="handlePerPageChange"
          ></view-component>
        </div>
      </div>
    </div>
    <TransitionGroup
      name="project-list"
      tag="div"
      class="container-cards-projects"
      :class="projectsLayout">
      <div
        v-for="(project, index) in projects"
        :key="project.id"
        class="project-card">
        <project-card :project="project"></project-card>
      </div>
      <div v-if="!projects.length && !isLoading" key="no-results" class="no-results-message">
        <p>{{trans('No projects were found that matched the current filters.')}}</p>
      </div>
    </TransitionGroup>

    <div class="container-pagination">
      <pagination
        :current-page="pagination.current_page"
        :per-page="pagination.per_page"
        :last-page="Math.ceil(pagination.total / pagination.per_page)"
        :total="pagination.total"
        @update:currentPage="pagination.current_page = $event"
      ></pagination>
    </div>
  </div>
</template>

<style scoped>


</style>
